import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { addNewGame, getGamesData } from "../redux/asyncThunk/userActions";
import { useNavigate } from "react-router-dom";
import { sanitizePlainText } from "../utils/inputValidation";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import { CONTENT_TYPES } from "../utils/contentType";
import { userSlice } from "../redux/user/userSlice";
import { markTrainerProjectHint } from "../utils/trainerProjectHint";
import { markKbProjectHint } from "../utils/kbProjectHint";

const { selectProject } = userSlice;

const TYPE_HINTS = {
  [CONTENT_TYPES.scenario]:
    "Интерактивный сценарий с видео и ветвлением по выбору.",
  [CONTENT_TYPES.trainer]:
    "После создания добавляйте вопросы, отмечайте правильные ответы и публикуйте тренажёр.",
  [CONTENT_TYPES.knowledge_base]:
    "После создания наполняйте тему разделами: пары «вопрос — ответ» для сотрудников в боте.",
};

export default function NewGame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const games = useSelector((state) => state.user?.games);
  const user = useSelector((state) => state.user?.data?.user);

  const [textProject, setTextProject] = useState("");
  const [contentType, setContentType] = useState(CONTENT_TYPES.scenario);

  const checkGamesTitle = useCallback(
    (count) => !games.find((el) => el.title === `Новый проект-${count}`),
    [games]
  );

  useEffect(() => {
    if (contentType !== CONTENT_TYPES.scenario) return;

    if (!games?.length) {
      setTextProject("Новый проект-1");
    } else {
      let count = 1;
      while (!checkGamesTitle(count)) {
        count++;
      }
      setTextProject(`Новый проект-${count}`);
    }
  }, [checkGamesTitle, games, contentType]);

  useEffect(() => {
    if (contentType === CONTENT_TYPES.scenario) return;
    setTextProject("");
  }, [contentType]);

  const isTrainer = contentType === CONTENT_TYPES.trainer;
  const isKnowledgeBase = contentType === CONTENT_TYPES.knowledge_base;

  const dialogTitle = isTrainer
    ? "Новый тренажёр"
    : isKnowledgeBase
      ? "Новая тема базы знаний"
      : "Создайте проект";

  const nameLabel = isTrainer
    ? "Название тренажёра"
    : isKnowledgeBase
      ? "Название темы"
      : "Название проекта";

  const namePlaceholder = isKnowledgeBase
    ? "Например: адаптация и HR-процессы"
    : isTrainer
      ? "Например: продуктовые знания"
      : undefined;

  const createDisabled = !textProject.trim();

  return (
    <div className="app-dialog__body">
      <div className="app-dialog__title">{dialogTitle}</div>

      <span className="app-dialog__label">Тип проекта</span>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={contentType}
        onChange={(_, value) => {
          if (value) setContentType(value);
        }}
        className="app-dialog__type-toggle app-dialog__type-toggle--three"
      >
        <ToggleButton value={CONTENT_TYPES.scenario}>
          <MovieCreationOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Сценарий
        </ToggleButton>
        <ToggleButton value={CONTENT_TYPES.trainer}>
          <QuizOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          Тренажёр
        </ToggleButton>
        <ToggleButton value={CONTENT_TYPES.knowledge_base}>
          <MenuBookOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
          База знаний
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        label={nameLabel}
        value={textProject}
        size="small"
        fullWidth
        sx={AUTH_FIELD_SX}
        placeholder={namePlaceholder}
        onChange={(ev) =>
          setTextProject(sanitizePlainText(ev.target.value, 120))
        }
      />

      <p className="app-dialog__hint">{TYPE_HINTS[contentType]}</p>

      <div className="app-dialog__actions">
        <Button
          variant="contained"
          className="app-btn app-btn--primary"
          disabled={createDisabled}
          onClick={() => {
            const trimmedName = textProject.trim();

            dispatch(
              addNewGame({
                userId: user.id,
                textProject: trimmedName,
                contentType,
                trainerTheme: isTrainer ? trimmedName : "",
                kbTheme: isKnowledgeBase ? trimmedName : "",
                onSuccess: (createdId) => {
                  dispatch(getGamesData({ id: user.id }));
                  if (createdId) {
                    if (isTrainer) {
                      markTrainerProjectHint(createdId);
                    }
                    if (isKnowledgeBase) {
                      markKbProjectHint(createdId);
                    }
                    dispatch(selectProject(createdId));
                    const typeQuery = isTrainer
                      ? "?type=trainer"
                      : isKnowledgeBase
                        ? "?type=knowledge_base"
                        : "";
                    navigate(`/main/${createdId}${typeQuery}`, { replace: true });
                  } else {
                    navigate("/main", { replace: true });
                  }
                },
              })
            );
          }}
        >
          Создать
        </Button>
      </div>
    </div>
  );
}
